import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/apiClient";
import { PageHeader } from "@/components/Common";
import { ComplianceNote } from "@/components/AppShell";
import { toast } from "sonner";
import { Upload, Save, Building2 } from "lucide-react";

const Field = ({ label, value, onChange, testid, placeholder, type = "text", hint }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-600">{label}</span>
    <input data-testid={testid} type={type} value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
    {hint && <span className="text-[11px] text-slate-400 mt-1 block">{hint}</span>}
  </label>
);

export default function Settings() {
  const [p, setP] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const fileRef = useRef(null);

  const loadLogo = async (path) => {
    if (!path) return setLogoUrl(null);
    try {
      const res = await fetch(`${API}/files/${path}`, { credentials: "include" });
      if (!res.ok) return;
      setLogoUrl(URL.createObjectURL(await res.blob()));
    } catch { /* noop */ }
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => { setP(data); loadLogo(data.logo_path); }).catch(() => {});
  }, []);

  if (!p) return <div className="text-sm text-slate-500">Loading…</div>;
  const set = (k) => (v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    try {
      await api.put("/profile", { ...p, gst_rate: Number(p.gst_rate) || 0 });
      toast.success("Business profile saved");
    } catch {
      toast.error("Could not save profile");
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((s) => ({ ...s, logo_path: data.logo_path }));
      loadLogo(data.logo_path);
      toast.success("Logo uploaded");
    } catch {
      toast.error("Logo upload failed");
    }
  };

  return (
    <div data-testid="settings-page" className="max-w-4xl">
      <PageHeader title="Business Profile" subtitle="Your details appear on every quote and tax invoice">
        <button data-testid="save-profile-btn" onClick={save}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors active:scale-95">
          <Save size={16} /> Save
        </button>
      </PageHeader>

      <div className="space-y-6">
        <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Building2 size={17} /> Business details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Business name" value={p.business_name} onChange={set("business_name")} testid="business-name-input" />
            <Field label="ABN *" value={p.abn} onChange={set("abn")} testid="abn-input"
              placeholder="12 345 678 901" hint="Required to issue compliant tax invoices." />
            <Field label="Email" value={p.email} onChange={set("email")} testid="business-email-input" type="email" />
            <Field label="Phone" value={p.phone} onChange={set("phone")} testid="business-phone-input" />
            <Field label="Website" value={p.website} onChange={set("website")} testid="business-website-input" />
            <Field label="Quote number prefix" value={p.quote_prefix} onChange={set("quote_prefix")} testid="quote-prefix-input" />
            <div className="sm:col-span-2">
              <Field label="Business address" value={p.address} onChange={set("address")} testid="business-address-input"
                placeholder="12 Smith St, Collingwood VIC 3066" />
            </div>
          </div>
          {!(p.abn || "").trim() && (
            <p className="mt-4 text-xs text-red-600" data-testid="abn-required-note">
              Add your ABN to unlock ATO-compliant tax invoicing.
            </p>
          )}
        </div>

        <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5">
          <h2 className="font-semibold text-slate-900 mb-4">Logo</h2>
          <div className="flex items-center gap-5">
            <div className="h-20 w-20 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Business logo" data-testid="logo-preview" className="h-full w-full object-contain" />
              ) : (
                <span className="text-xs font-black tracking-tighter text-slate-400">WVT</span>
              )}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
              <button data-testid="upload-logo-btn" onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-md px-4 py-2 text-sm font-medium transition-colors">
                <Upload size={15} /> Upload logo
              </button>
              <p className="text-xs text-slate-400 mt-2">PNG, JPG, WEBP or SVG up to 5MB.</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5">
          <h2 className="font-semibold text-slate-900 mb-4">Tax &amp; currency defaults</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="GST rate %" value={p.gst_rate} onChange={set("gst_rate")} testid="gst-rate-setting-input" type="number" />
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Currency</span>
              <select data-testid="currency-select" value={p.currency} onChange={(e) => set("currency")(e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="AUD">AUD — Australian Dollar</option>
                <option value="NZD">NZD — New Zealand Dollar</option>
                <option value="USD">USD — US Dollar</option>
              </select>
            </label>
            <label className="flex items-start gap-2.5 sm:col-span-2">
              <input data-testid="default-inclusive-toggle" type="checkbox" checked={!!p.prices_include_gst}
                onChange={(e) => set("prices_include_gst")(e.target.checked)} className="h-4 w-4 mt-0.5 accent-blue-600" />
              <span className="text-xs text-slate-700 leading-relaxed">
                New documents default to <strong>GST-inclusive</strong> pricing — consumers must see the total price payable.
              </span>
            </label>
          </div>
        </div>

        <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Default cover note</span>
            <textarea data-testid="default-cover-note-input" rows={4} value={p.default_cover_note || ""}
              onChange={(e) => set("default_cover_note")(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Default terms &amp; conditions</span>
            <textarea data-testid="default-terms-input" rows={4} value={p.default_terms || ""}
              onChange={(e) => set("default_terms")(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </label>
        </div>

        <ComplianceNote />
      </div>
    </div>
  );
}
